import { useEffect, useState } from 'react';

import { type SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import logo from '@/assets/logo.svg';
import { BoxedIconMessage } from '@/components/BoxedIconMessage';
import PasswordInput from '@/components/PasswordInput/PasswordInput';
import { ToastNotification } from '@/components/ToastNotification';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { StyledP } from '@/Shared/Typography/typography';

import {
	StyledBG,
	StyledFeatherBackgroundBottom,
	StyledFeatherBackgroundTop,
	StyledForm,
} from './SetPasswordStyles';

interface FormValues {
	password: string;
	passwordConfirmation: string;
}

const EXPIRED_TOKEN_MESSAGE = `Your request to set your password has expired or the link has already been used. Click <a href="/reset-password" style="color: var(--body-text)">here</a> to send yourself an email to reset your password.`;

const SetPassword = () => {
	const {
		register,
		handleSubmit,
		watch,
		formState: { errors, isSubmitting },
	} = useForm<FormValues>();
	const [messageState, setMessageState] = useState({
		message: '',
		messageColor: 'var(--green-300)',
		hasError: false,
	});
	const navigate = useNavigate();

	// Check for Supabase recovery token in URL on mount
	// Note: We don't verify the token here - we just check if it exists
	// The actual verification happens in onSubmit to avoid consuming the token prematurely
	useEffect(() => {
		const handleRecovery = async () => {
			const hashParams = new URLSearchParams(window.location.hash.substring(1));
			const accessToken = hashParams.get('access_token');
			const refreshToken = hashParams.get('refresh_token');
			const tokenHash = hashParams.get('token_hash');
			const type = hashParams.get('type');

			// Also check query params for token (for custom redirects)
			const urlParams = new URLSearchParams(window.location.search);
			const queryToken = urlParams.get('token');

			// Check if we have any valid token
			const hasToken = accessToken || tokenHash || queryToken;

			if (hasToken && (type === 'recovery' || urlParams.get('type') === 'recovery')) {
				// If we have both JWT tokens, use setSession
				if (accessToken && refreshToken) {
					const { error: exchangeError } = await supabase.auth.setSession({
						access_token: accessToken,
						refresh_token: refreshToken,
					});

					if (exchangeError) {
						setMessageState({
							message: EXPIRED_TOKEN_MESSAGE,
							messageColor: 'var(--red-200)',
							hasError: true,
						});
					}
				}
				// If we have token_hash or access_token only, we'll handle it in onSubmit using verifyOtp
				// Don't verify here to avoid consuming the one-time token
			} else if (!hasToken) {
				// Check for legacy Firebase format (oobCode) for backward compatibility
				const oobCode = urlParams.get('oobCode');
				if (!oobCode) {
					setMessageState({
						message: EXPIRED_TOKEN_MESSAGE,
						messageColor: 'var(--red-200)',
						hasError: true,
					});
				}
			}
		};

		handleRecovery();
	}, []);

	const onSubmit: SubmitHandler<FormValues> = async (passwordFormData) => {
		const { password } = passwordFormData;

		const url = new URL(window.location.href);
		const searchParams = new URLSearchParams(url.search);
		const hashParams = new URLSearchParams(window.location.hash.substring(1));

		// Check for Supabase tokens (in hash or query params)
		const accessToken = hashParams.get('access_token');
		const refreshToken = hashParams.get('refresh_token');
		const tokenHash = hashParams.get('token_hash') || searchParams.get('token');
		const type = searchParams.get('type') || hashParams.get('type');

		// Legacy Firebase support (for backward compatibility)
		const oobCode = searchParams.get('oobCode');
		const apiKey = searchParams.get('apiKey');
		const email = searchParams.get('email');

		try {
			// Handle Supabase password reset
			if ((accessToken || tokenHash) && type === 'recovery') {
				// If we have both JWT tokens (access_token + refresh_token), use setSession
				if (accessToken && refreshToken) {
					const { error: sessionError } = await supabase.auth.setSession({
						access_token: accessToken,
						refresh_token: refreshToken,
					});

					if (sessionError) {
						setMessageState({
							message: EXPIRED_TOKEN_MESSAGE,
							messageColor: 'var(--red-200)',
							hasError: true,
						});
						return;
					}
				} else {
					// We have a token_hash (from generateLink) - use verifyOtp
					// The token could be in token_hash param or access_token param (legacy)
					const verifyToken = tokenHash || accessToken || '';

					const { error: verifyError } = await supabase.auth.verifyOtp({
						token_hash: verifyToken,
						type: 'recovery',
					});

					if (verifyError) {
						setMessageState({
							message: EXPIRED_TOKEN_MESSAGE,
							messageColor: 'var(--red-200)',
							hasError: true,
						});
						return;
					}

					// verifyOtp succeeded, we should now have a session
					// Continue to update password below
				}

				// Update the password
				const { error: updateError, data: updateData } = await supabase.auth.updateUser({
					password,
				});

				if (updateError?.code === 'same_password') {
					setMessageState({
						message: 'Your new password cannot be the same as your old password.',
						messageColor: 'var(--red-200)',
						hasError: true,
					});
					return;
				}

				if (updateError) {
					setMessageState({
						message: 'Something went wrong. Please try again.',
						messageColor: 'var(--red-200)',
						hasError: true,
					});
					return;
				}

				// Password updated successfully, redirect to dashboard
				// After updateUser succeeds, verify session once
				if (updateData?.user) {
					setTimeout(() => {
						navigate('/dashboard/courses');
					}, 1000);
				}
			} else if (oobCode && apiKey) {
				// Legacy Firebase support
				const response = await fetch(
					`https://www.googleapis.com/identitytoolkit/v3/relyingparty/resetPassword?key=${apiKey}`,
					{
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({
							oobCode,
							newPassword: password,
						}),
					}
				);

				if (response.status === 400) {
					setMessageState({
						message: EXPIRED_TOKEN_MESSAGE,
						messageColor: 'var(--red-200)',
						hasError: true,
					});
					return;
				}

				if (email) {
					// Sign in with Firebase (legacy)
					const { getAuth, signInWithEmailAndPassword } = await import('firebase/auth');
					const auth = getAuth();
					await signInWithEmailAndPassword(auth, email, password);
					navigate('/dashboard/courses');
				}
			} else {
				setMessageState({
					message: EXPIRED_TOKEN_MESSAGE,
					messageColor: 'var(--red-200)',
					hasError: true,
				});
			}
		} catch (error) {
			setMessageState({
				message: 'Something went wrong. Please try again.',
				messageColor: 'var(--red-200)',
				hasError: true,
			});
		}
	};

	return (
		<StyledBG>
			<StyledFeatherBackgroundTop />
			<StyledFeatherBackgroundBottom />
			<img src={`${logo}`} alt="Elevate logo" className="logo" />
			<StyledForm onSubmit={handleSubmit(onSubmit)} data-testid="form">
				<StyledP mb="var(--spacing-4)">
					<strong>Set your password below.</strong> You will be redirected once your password is
					successfully set.
				</StyledP>
				<PasswordInput
					labelText="New Password"
					registerOptions={register('password', {
						required: 'Password is required',
						minLength: {
							value: 8,
							message: 'Password must be at least 8 characters',
						},
					})}
					errors={errors}
				/>
				<PasswordInput
					labelText="Confirm Password"
					registerOptions={register('passwordConfirmation', {
						required: 'Password is required',
						validate: (val: string) => {
							if (watch('password') !== val) {
								return 'Your passwords do not match.';
							}
							return true;
						},
						minLength: {
							value: 8,
							message: 'Password must be at least 8 characters',
						},
					})}
					errors={errors}
				/>
				<Button type="submit" disabled={isSubmitting} className="w-max">
					Set Password
				</Button>
				{messageState.message !== '' && (
					<BoxedIconMessage
						message={messageState.message}
						messageColor={messageState.messageColor}
						hasError={messageState.hasError}
					/>
				)}
			</StyledForm>
			<ToastNotification />
		</StyledBG>
	);
};

export default SetPassword;
